/**
 * @file td-desktop-menu-handler.js
 * @description Handles all desktop navigation menu functionality
 * @version 1.0.0
 */

if (!customElements.get("td-desktop-navigation")) {
    class TDDesktopNavigation extends HTMLElement {
        constructor() {
            super();

            this.state = {
                activeMenu: null,
                activeDetails: null,
                activeMenuType: null,
                activeTrigger: null
            };

            this.isHandlingToggle = false;

            this._boundHandlers = {
                globalClick: null,
                toggleHandlers: new Map(),
                clickHandlers: new Map()
            };
        }

        connectedCallback() {
            this.elements = {
                menuTriggers: this.querySelectorAll('summary.td-nav'),
                dropdownTriggers: this.querySelectorAll('summary.site-nav__link--has-dropdown'),
                allMenuTriggers: this.querySelectorAll('summary.td-nav, summary.site-nav__link--has-dropdown'),
                megaMenus: this.querySelectorAll('.td-megamenu'),
                detailsElements: this.querySelectorAll('details.site-nav__details'),
                navBar: this.querySelector('.td-navigation'),
                popup: document.querySelector("#ps__widget")
            };

            this.setupEventListeners();
        }

        disconnectedCallback() {
            this.cleanupEventListeners();
        }

        cleanupEventListeners() {
            if (this._boundHandlers.globalClick) {
                document.removeEventListener('click', this._boundHandlers.globalClick, true);
                this._boundHandlers.globalClick = null;
            }

            this._boundHandlers.toggleHandlers.forEach((handler, details) => {
                details.removeEventListener('toggle', handler);
            });
            this._boundHandlers.toggleHandlers.clear();

            this._boundHandlers.clickHandlers.forEach((handler, trigger) => {
                trigger.removeEventListener('click', handler);
            });
            this._boundHandlers.clickHandlers.clear();
        }

        setupEventListeners() {
            this.cleanupEventListeners();

            this._boundHandlers.globalClick = (e) => {
                if (this.isHandlingToggle) return;

                const clickedTrigger = e.target.closest('summary.td-nav, summary.site-nav__link--has-dropdown');
                
                if (clickedTrigger) return;
                if (this.state.activeMenu && this.state.activeMenu.contains(e.target)) return;
                if (this.elements.navBar && this.elements.navBar.contains(e.target)) return;

                this.closeAllMenus();
            };
            document.addEventListener('click', this._boundHandlers.globalClick, true);

            this.elements.detailsElements.forEach(details => {
                const toggleHandler = (e) => {
                    const summary = details.querySelector('summary');
                    if (summary && (summary.classList.contains('td-nav') || summary.classList.contains('site-nav__link--has-dropdown'))) {
                        if (!this.isHandlingToggle) {
                            e.preventDefault();
                            if (this.state.activeDetails === details && this.state.activeMenuType === 'dropdown') {
                                details.setAttribute('open', '');
                            } else {
                                details.removeAttribute('open');
                            }
                        }
                    }
                };
                details.addEventListener('toggle', toggleHandler);
                this._boundHandlers.toggleHandlers.set(details, toggleHandler);
            });

            this.elements.allMenuTriggers.forEach(trigger => {
                const clickHandler = (e) => this.handleMenuToggle(e);
                trigger.addEventListener('click', clickHandler);
                this._boundHandlers.clickHandlers.set(trigger, clickHandler);
            });
        }

        handleMenuToggle(e) {
            this.isHandlingToggle = true;
            e.preventDefault();
            e.stopPropagation();

            let triggerElement = e.target;

            if (triggerElement.tagName !== 'SUMMARY') triggerElement = triggerElement.closest('summary');

            if (!triggerElement) {
                triggerElement = e.target.closest('[data-link]');
                if (triggerElement && triggerElement.tagName !== 'SUMMARY') triggerElement = triggerElement.closest('summary');
            }

            if (!triggerElement) {
                console.warn('No trigger element found for click:', e.target);
                this.isHandlingToggle = false;
                return;
            }

            const linkId = triggerElement.dataset.link;
            if (!linkId) {
                console.warn('No linkId found for trigger:', triggerElement);
                this.isHandlingToggle = false;
                return;
            }

            const details = triggerElement.closest('details');
            const isMegaMenu = triggerElement.classList.contains('td-nav');

            let menuElement;
            if (isMegaMenu) {
                menuElement = document.querySelector('#mega-' + linkId);
                if (!menuElement) {
                    console.warn('No megamenu found for linkId:', linkId);
                    this.isHandlingToggle = false;
                    return;
                }
            } else {
                menuElement = details;
                if (!menuElement) {
                    console.warn('No details element found for dropdown:', triggerElement);
                    this.isHandlingToggle = false;
                    return;
                }
            }

            const isCurrentlyActive = isMegaMenu
                ? !menuElement.classList.contains('td-hide')
                : details?.hasAttribute('open') === true;

            if (menuElement !== this.state.activeMenu || !isCurrentlyActive) {
                this.closeAllMenus(menuElement);

                this.state.activeMenu = menuElement;
                this.state.activeDetails = details;
                this.state.activeMenuType = isMegaMenu ? 'mega' : 'dropdown';
                this.state.activeTrigger = triggerElement;
                
                this.toggleActiveState(triggerElement, true);
                this.toggleMenu(menuElement, true, details);

                if (isMegaMenu) this.togglePopup(false);
            } else {
                this.toggleActiveState(triggerElement, false);
                this.toggleMenu(menuElement, false, details);
                this.closeAllMenus();

                if (isMegaMenu) this.togglePopup(true);
            }

            setTimeout(() => {
                this.isHandlingToggle = false;
            }, 0);
        }

        toggleActiveState(element, isActive) {
            if (!element) return;

            const icon = element.querySelector('svg.td-icon') || element.querySelector('svg');
            
            if (isActive) {
                element.classList.add('td-nav-active--text');
                if (icon) icon.classList.add('td-nav-active');
            } else {
                element.classList.remove('td-nav-active--text');
                if (icon) icon.classList.remove('td-nav-active');
            }
        }

        toggleMenu(menu, show, details = null) {
            if (!menu) return;
            
            if (show) {
                if (menu.classList.contains('td-megamenu')) menu.classList.remove('td-hide');
                if (details) details.setAttribute('open', '');
            } else {
                if (menu.classList.contains('td-megamenu')) menu.classList.add('td-hide');
                if (details) details.removeAttribute('open');
            }
        }

        closeAllMenus(exceptMenu = null) {
            let exceptTrigger = null;
            if (exceptMenu && this.state.activeTrigger) {
                if (exceptMenu === this.state.activeMenu) exceptTrigger = this.state.activeTrigger;
            }

            this.elements.megaMenus.forEach(menu => {
                if (menu !== exceptMenu) menu.classList.add('td-hide');
            });

            this.elements.detailsElements.forEach(details => {
                const summary = details.querySelector('summary');
                const isException = exceptMenu === details ||
                                   (exceptMenu && exceptMenu.classList.contains('td-megamenu') && summary === exceptTrigger);

                if (!isException) details.removeAttribute('open');
            });

            this.elements.allMenuTriggers.forEach(trigger => {
                if (trigger !== exceptTrigger) this.toggleActiveState(trigger, false);
            });

            if (!exceptMenu) {
                this.state.activeMenu = null;
                this.state.activeDetails = null;
                this.state.activeMenuType = null;
                this.state.activeTrigger = null;
            }
        }

        togglePopup(show) {
            if (!this.elements.popup) return;
            this.elements.popup.style.display = show ? 'block' : 'none';
        }
    }

    customElements.define("td-desktop-navigation", TDDesktopNavigation);
}
